import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  getNotifications,
  selectCurrentTeam,
  selectCurrentTeamCustomersPermissions,
  selectMerchantTeams,
  setCurrentTeam,
  showNotifier
} from 'containers/app/appSlice';
import { CustomButton, If, Loader, NoPermissions, Pagination, SearchPopover } from 'components';
import { ButtonType } from 'components/custom-button/CustomButton';
import { usePaginatedGetCustomersV2Query } from './hooks/usePaginatedGetCustomersV2Query';
import { debounce } from 'utils/commonUtils';
import { useAppSelector } from 'hooks';
import { AddCustomerIcon, InvoicesPageIcon, NoSearchResultsIcon, SearchIcon } from 'assets/icons';
import { selectCurrentCustomerView } from './customersSlice';
import CustomersTable from 'containers/customers/components/CustomersTable';
import { useLazyGetCustomerByIdQuery } from 'containers/invoices/api';
import { CreateCustomerModalTypes } from 'constants/CustomerConstants';
import CreateCustomer from 'containers/create-customer/CreateCustomer';
import { logAnalyticEvent } from 'utils/analytics';
import { clevertapEvents } from 'types/baseTypes';
import { InfoMesssages } from 'types/infoMessages';
import { NotifierTypes } from 'constants/notifierConstants';
import { RoutePath } from 'routes/index';
import { ROOT_ROUTE_STATE } from 'routes/constants';
import { resetInvoiceState } from 'containers/invoices/InvoicesSlice';
import routesPath from 'routes/RoutesPath';
import {
  useCreateCustomerMutation,
  useGetCustomerViewsQuery,
  useLazyDownloadCustomerListQuery,
  useUpdateCustomerMutation
} from './api';
import { API_SOURCE } from 'constants/common';
import { CreateCustomerPayload, GetCustomersV2ColumnInfo, UpdateCustomerPayload } from './customers.model';
import CustomerFilters from './components/CustomersFilter';
import CustomerViews from './components/CustomerViews';
import CustomersTableActions from './components/CustomersTableActions';
import CustomizeColumnsModal, { ColumnConfig } from 'components/customize-columns-modal/CustomizeColumnsModal';
import { useSaveCustomColumnsMutation } from 'containers/accounting/pay-runs/api';
import { formatCustomerFilterParamsForAPI } from './Utils';

interface CustomersProps {
  className?: string;
}

const ITEMS_PER_PAGE = 50;
const PAGE_SIZE_STORAGE_KEY = 'scanpay-page-size';

const Customers: FC<CustomersProps> = () => {
  const currentTeam = useAppSelector(selectCurrentTeam);
  const merchantTeams = useAppSelector(selectMerchantTeams);
  const customerPermissions = useAppSelector(selectCurrentTeamCustomersPermissions);
  const currentView = useAppSelector(selectCurrentCustomerView);
  const notifications = useAppSelector(getNotifications);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { teamId } = useParams();

  const getInitialPageSize = (): number => {
    try {
      const storedPageSize = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
      if (storedPageSize) {
        const parsed = parseInt(storedPageSize, 10);
        if (!isNaN(parsed) && parsed > 0) {
          return parsed;
        }
      }
    } catch (error) {
      //
    }
    return ITEMS_PER_PAGE;
  };

  const initialPageSize = getInitialPageSize();

  const [openCreateCustomerModal, setOpenCreateCustomerModal] = useState(false);
  const [createCustomerModalType, setCreateCustomerModalType] = useState<{ type: CreateCustomerModalTypes }>({
    type: CreateCustomerModalTypes.CREATE_CUSTOMER
  });
  const [searchText, setSearchText] = useState('');
  const [currentCustomerId, setCurrentCustomerId] = useState('');
  const [showEditTableModal, setShowEditTableModal] = useState(false);
  const [searchIconEnlarged, setSearchIconEnlarged] = useState(false);

  const [createCustomer, { isLoading: isCreatingCustomer }] = useCreateCustomerMutation();
  const [updateCustomer, { isLoading: isUpdatingCustomer }] = useUpdateCustomerMutation();
  const [downloadCustomerList] = useLazyDownloadCustomerListQuery();
  const [saveCustomColumns, { isLoading: isSavingColumns }] = useSaveCustomColumnsMutation();
  const { data, isLoading } = useGetCustomerViewsQuery(
    { teamId: currentTeam?.id?.toString() ?? '' },
    { skip: !currentTeam?.id }
  );

  const {
    list: customersList,
    isLoading: isFetchingCustomers,
    fetchData,
    resetQuery: resetCustomersQuery,
    setQueryParams: setCustomersQueryParams,
    queryParams: customersQueryParams,
    totalListCount: customersCount,
    hasPermissionsToView,
    columnConfig,
    page,
    isLoadingSuccess: isCustomersLoadingSuccess
  } = usePaginatedGetCustomersV2Query(initialPageSize);

  const handleSearchIconClick = () => {
    setSearchIconEnlarged(true);
  };

  const closeCreateCustomerModal = () => {
    setOpenCreateCustomerModal(false);
    setCurrentCustomerId(null);
  };

  useEffect(() => {
    if (teamId && currentTeam?.id !== +teamId) {
      const merchantTeam = merchantTeams.find(team => team.id === +teamId);
      if (merchantTeam) {
        dispatch(setCurrentTeam(merchantTeam));
        dispatch(resetInvoiceState());
      } else {
        navigate(routesPath.CUSTOMERS, { replace: true });
      }
    }
  }, [location]);

  useEffect(() => {
    resetCustomersQuery();
  }, [currentTeam?.id]);

  useEffect(() => {
    resetCustomersQuery();
  }, [
    customerPermissions.viewCustomersCreatedByTeam,
    customerPermissions.editCustomersCreatedByTeam,
    customerPermissions.editExistingCustomers
  ]);

  const [getCustomerByIdQuery, getCustomerByIdState] = useLazyGetCustomerByIdQuery();
  const { data: customerData, isSuccess: customerDataSuccess, isFetching: fetchingCustomerData } = getCustomerByIdState;

  const handleSidePanelClose = () => {
    navigate(RoutePath.CUSTOMERS);
  };

  const handleCustomerClick = (customerId: string) => {
    navigate(`/customers/${currentTeam?.id}/${customerId}/customer`, { state: ROOT_ROUTE_STATE });
  };

  const handleCustomerCreation = (id: string) => {
    logAnalyticEvent(clevertapEvents.webCustomersCreateCustomer);
    if (!customerPermissions.createNewCustomers) {
      onInfoClick(id, InfoMesssages.createCustomerNoPermissionMessage);
      return;
    }
    setCreateCustomerModalType({ type: CreateCustomerModalTypes.CREATE_CUSTOMER });
    setOpenCreateCustomerModal(true);
  };

  const onEditCustomer = (customerId: string) => {
    setCreateCustomerModalType({ type: CreateCustomerModalTypes.EDIT_CUSTOMER });
    getCustomerByIdQuery({ teamId: currentTeam.id, customerId: customerId });
    setCurrentCustomerId(customerId);
    setOpenCreateCustomerModal(true);
  };

  const initiateSearch = useCallback((searchTerm?: string) => {
    setCustomersQueryParams(prevQueryParams => ({ ...prevQueryParams, search: searchTerm ?? null }));
  }, []);

  const debouncedSearchHandler = useCallback(debounce(initiateSearch, 500), [initiateSearch]);

  const onSearchTermChange = (searchTerm: string) => {
    setSearchText(searchTerm);
    debouncedSearchHandler(searchTerm);
  };

  const onClearSearch = () => {
    setSearchText('');
    initiateSearch(null);
  };

  const handleCreateOrEditCustomer = async customerPayload => {
    const payload: CreateCustomerPayload | UpdateCustomerPayload = {
      ...customerPayload,
      source: API_SOURCE.WEB
    };
    if (currentCustomerId) await updateCustomer(payload as UpdateCustomerPayload);
    else await createCustomer(payload as CreateCustomerPayload);
    closeCreateCustomerModal();
  };

  const onInfoClick = (id: string, infoMessage) => {
    const existingNotification = notifications.find(notification => notification.id === id);
    if (existingNotification) return;
    dispatch(
      showNotifier({
        id,
        message: {
          primaryMessage: infoMessage,
          secondaryMessage: '',
          isMessageHtml: true
        },
        type: NotifierTypes.WARNING,
        showClose: true,
        fontStyle: 'text-primary font-normal'
      })
    );
  };

  const handleFiltersChange = (filters: any) => {
    setCustomersQueryParams(prevParams => ({
      ...prevParams,
      ...filters
    }));
  };

  const handlePageChange = (pageNumber: number) => {
    fetchData(pageNumber);
  };

  const handleSortChangeWrapper = (sortByParam: string, sortOrderParam: string) => {
    setCustomersQueryParams(prevParams => {
      return {
        ...prevParams,
        sortBy: sortByParam,
        sortOrder: sortOrderParam
      };
    });
  };

  const onViewChange = ({
    viewId,
    columns,
    sortBy,
    sortOrder
  }: {
    viewId: string;
    columns: string[];
    sortBy: string;
    sortOrder: string;
  }) => {
    setCustomersQueryParams(prevQueryParams => ({
      ...prevQueryParams,
      viewId,
      focusedColumns: columns,
      sortBy,
      sortOrder
    }));
  };

  const handleEditTableClick = () => {
    setShowEditTableModal(true);
  };

  const handleEditTableModalClose = () => {
    setShowEditTableModal(false);
  };

  const handleEditTableSave = async (columns: ColumnConfig[]) => {
    if (!currentTeam?.id) return;

    try {
      await saveCustomColumns({
        teamId: currentTeam.id,
        payload: {
          feature: `CUSTOMERS_${currentView?.id}`,
          columnInfo: columns.map(col => ({
            columnKey: col.columnKey,
            columnLabel: col.columnLabel,
            isEnabled: col.isEnabled,
            priority: col.priority,
            showAlways: col.showAlways,
            isFocused: col.isFocused
          }))
        }
      }).unwrap();

      handleEditTableModalClose();
    } catch (error) {
      //
    }
  };

  const handleSelectResultsPerPage = (pageSize: number) => {
    try {
      localStorage.setItem(PAGE_SIZE_STORAGE_KEY, pageSize.toString());
    } catch (error) {
      //
    }
    setCustomersQueryParams(prevQueryParams => ({
      ...prevQueryParams,
      limit: pageSize
    }));
  };

  const getFormattedCustomerData = useCallback(() => {
    if (!currentCustomerId || !customerData) {
      return null;
    }

    return {
      ID: customerData?.id,
      name: customerData?.name,
      lastName: customerData?.lastName,
      email: customerData?.email,
      phoneNumber: customerData?.phoneNumber,
      companyName: customerData?.companyName,
      referenceNumber: customerData?.referenceNumber,
      note: customerData?.note
    };
  }, [currentCustomerId, customerData]);

  const handleExport = async () => {
    if (!currentTeam?.id) return;
    dispatch(
      showNotifier({
        message: {
          primaryMessage: 'Downloading started in the background',
          secondaryMessage: ''
        },
        type: NotifierTypes.INFO,
        showClose: false,
        fontStyle: 'text-primary font-normal'
      })
    );
    const finalQueryParams = formatCustomerFilterParamsForAPI(customersQueryParams);
    await downloadCustomerList({
      teamId: currentTeam.id.toString(),
      params: {
        viewId: currentView?.id.toString(),
        focusedColumns: customersQueryParams.focusedColumns,
        ...finalQueryParams
      }
    }).unwrap();
  };

  const getCustomersColumn = useMemo(() => {
    return columnConfig.map(col => ({
      columnKey: col.columnKey,
      columnLabel: col.columnLabel,
      isEnabled: col.isEnabled,
      priority: col.priority,
      showAlways: col.showAlways,
      isFocused: col.isFocused
    }));
  }, [columnConfig]);

  const hasFiltersApplied = useMemo(() => {
    const filterKeys = [
      'lastActivityAtFrom',
      'lastActivityAtTo',
      'cardOnFileCount',
      'totalInvoicedAmount',
      'outstandingBalanceAmount',
      'totalPaidAmount',
      'disputeLossAmount',
      'refundAmount',
      'jobCount',
      'createdAtFrom',
      'createdAtTo',
      'createdBy'
    ] as const;
    return filterKeys.some(key => {
      const value = customersQueryParams[key];
      if (Array.isArray(value)) return value.length > 0;
      return value !== undefined && value !== null && value !== '';
    });
  }, [customersQueryParams]);

  return (
    <>
      <div className="overflow-y flex h-screen flex-col justify-between pt-6">
        <div className="mb-5 flex flex-row items-center gap-3 px-6">
          <div id="customer-page-customer-title" className="text-2xl font-bold text-primaryText">
            Customers
          </div>
          <If condition={!searchIconEnlarged}>
            <div onClick={handleSearchIconClick}>
              <SearchIcon className="mx-2 flex lg:hidden" />
            </div>
          </If>
          <SearchPopover
            searchId="customer-search"
            suggestionsList={[]}
            onClearSearchInput={onClearSearch}
            onSearchInputChange={onSearchTermChange}
            searchInputvalue={searchText}
            searchInputPlaceholder="Search Customer"
            wrapperStyle={`w-38 h-10 cursor-pointer ${!searchIconEnlarged ? 'lg:flex sm:hidden' : ''} `}
          />
          <If condition={customerPermissions.viewCustomersCreatedByTeam}>
            <div className="ml-auto">
              <CustomButton
                id="create-customer-button"
                onClick={() => handleCustomerCreation('create-customer-button')}
                className="h-[38px] text-primaryBtnText"
                showDisabledReason={!customerPermissions.createNewCustomers}
                StartIcon={AddCustomerIcon}
                childrenContainerStyles="mr-1">
                <div className="text-sbase font-semibold">Create customer</div>
              </CustomButton>
            </div>
          </If>
        </div>
        {/* Customer Views */}
        <div className="">
          <CustomerViews views={data?.viewList ?? []} isTeamViewsLoading={isLoading} handleViewChange={onViewChange} />
        </div>

        <div className="my-4 px-6">
          <CustomerFilters onFiltersChange={handleFiltersChange} />
        </div>

        <If condition={customersList?.length !== 0}>
          <div className="flex h-10 w-full items-center border-y border-borderGray bg-[#F4F4F4] py-1 px-[30px]">
            <CustomersTableActions onExportClick={handleExport} onEditTableClick={handleEditTableClick} />
          </div>
        </If>

        <div className="mt-top customNormalScroll grow pb-12">
          <If condition={isFetchingCustomers}>
            <div className="flex h-full w-full items-center justify-center">
              <Loader />
            </div>
          </If>
          <If
            condition={
              customersList?.length === 0 &&
              !isFetchingCustomers &&
              customersQueryParams.search === '' &&
              !hasFiltersApplied &&
              hasPermissionsToView &&
              isCustomersLoadingSuccess
            }>
            <div className="flex h-full flex-col items-center justify-center gap-5">
              <div className="flex flex-col items-center gap-5">
                <InvoicesPageIcon className="h-10 w-10" />
                <div className="text-sbase text-primaryText">Start creating your first customer</div>
                <CustomButton
                  id="create-first-customer-button"
                  onClick={() => handleCustomerCreation('create-first-customer-button')}
                  className={`bg-transparent text-sbase font-semibold normal-case 
                  text-secondary`}
                  type={ButtonType.TERTIARY}
                  showDisabledReason={!customerPermissions.createNewCustomers}>
                  Create customer
                </CustomButton>
              </div>
            </div>
          </If>

          <If
            condition={
              customersList?.length === 0 &&
              !isFetchingCustomers &&
              hasFiltersApplied &&
              hasPermissionsToView &&
              isCustomersLoadingSuccess
            }>
            <div className="flex h-full flex-col items-center justify-center gap-5">
              <div className="flex flex-col items-center gap-5">
                <NoSearchResultsIcon />
                <div className="text-sbase text-primaryText">No customers found</div>
              </div>
            </div>
          </If>

          <If condition={!isFetchingCustomers && !hasPermissionsToView}>
            <div className="flex h-full flex-col items-center justify-center gap-5">
              <NoPermissions />
            </div>
          </If>

          <If
            condition={
              customersList?.length === 0 &&
              !isFetchingCustomers &&
              customersQueryParams.search !== null &&
              customersQueryParams.search !== '' &&
              !hasFiltersApplied &&
              hasPermissionsToView
            }>
            <div className="flex h-full flex-col items-center justify-center gap-5">
              <div className="flex flex-col items-center gap-5">
                <NoSearchResultsIcon />
                <div className="text-sbase text-primaryText">No results found!</div>
              </div>
            </div>
          </If>
          <If condition={customersList?.length !== 0 && !isFetchingCustomers}>
            <CustomersTable
              customersList={customersList}
              onCustomerClick={handleCustomerClick}
              onEditCustomer={onEditCustomer}
              columnInfo={columnConfig}
              onSortChange={handleSortChangeWrapper}
              currentSortBy={customersQueryParams.sortBy}
              currentSortOrder={customersQueryParams.sortOrder}
              stickyHorizontalScrollbar={{ bottomOffsetPx: 48 }}
            />
          </If>
        </div>
        <If condition={customersList?.length > 0}>
          <Pagination
            currentPage={page}
            totalCount={customersCount}
            onPageChange={handlePageChange}
            limit={customersQueryParams.limit ?? initialPageSize}
            wrapperClassName="sticky bottom-0 w-full !h-12 py-2.5"
            showSelectResultsPerPage={true}
            onSelectResultsPerPage={handleSelectResultsPerPage}
          />
        </If>
      </div>
      <Outlet context={{ handleSidePanelClose, resetCustomersQuery }} />
      {showEditTableModal && (
        <CustomizeColumnsModal
          open={showEditTableModal}
          onClose={handleEditTableModalClose}
          onSave={columns => handleEditTableSave(columns as GetCustomersV2ColumnInfo[])}
          isLoading={isSavingColumns}
          columns={getCustomersColumn}
          reorderableStartIndex={1}
        />
      )}
      <If condition={Boolean(openCreateCustomerModal)}>
        <CreateCustomer
          open={openCreateCustomerModal}
          onClose={closeCreateCustomerModal}
          panelType={createCustomerModalType.type}
          customerData={getFormattedCustomerData()}
          customerDataFetchSuccess={customerDataSuccess}
          customerDataFetchLoading={fetchingCustomerData}
          onAddCustomer={handleCreateOrEditCustomer}
          isLoading={isCreatingCustomer || isUpdatingCustomer}
        />
      </If>
    </>
  );
};

export default Customers;
